import { requiredMessage } from "@/components/forms/requiredMessage.tsx";
import T from "@/components/forms/TextField.module.css";
import { WFakeForm } from "@/components/forms/WHookForm.tsx";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { cardListRequest } from "@/modules/billing/billingCardsQuery.ts";
import type { AddMethodProps } from "@/modules/billing/choose_method/AddMethodParams.ts";
import { addCardRequest, cardVerifyRequest } from "@/modules/billing/choose_method/addMethodUtils.ts";
import { FormBox } from "@/modules/billing/choose_method/FormBox.tsx";
import {
    omiseCreateToken,
    omiseLoader,
    usePreloadOmise,
    type FixedOmiseTokenParameters,
} from "@/modules/billing/omise/omiseLoader.ts";
import { pollForOmiseCardValidationSuccess } from "@/modules/billing/omise/parseOmisePersistentData.ts";
import {
    CardNameField,
    CardNumberField,
    ErrorText,
    StandardField,
} from "@/modules/billing/payment_forms/components.tsx";
import { cardCvcLabel, cardExpiryLabel } from "@/modules/billing/payment_forms/labels.tsx";
import {
    t_ConnectingPaymentMethod,
    t_LoadingPaymentProvider,
    t_RedirectingToPaymentGateway,
    t_SavingInformation,
    t_VerifyingPaymentMethod,
} from "@/modules/billing/progressLabels.tsx";
import { isBuiltRelease, isTestEnvironment } from "@/utils/environment.ts";
import { useUnmountSignal } from "@/utils/react/useUnmountSignal.tsx";
import { useLingui } from "@lingui/react/macro";
import type { OmiseCreditCardConfig } from "@warrenio/api-spec/spec.oats.gen";
import { useImperativeHandle, useState } from "react";
import { Input, Label, TextField } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import invariant from "tiny-invariant";

interface OmiseCardFormInputs {
    name: string;
    number: string;
    expiration: string;
    security_code: string;
}

const _visaTest = {
    name: "Test Success (Visa)",
    number: "4111 1111 1111 1111",
    expiration: "12/34",
    security_code: "123",
} satisfies Partial<OmiseCardFormInputs>;

const _visa3dsValidationFailedTest = {
    name: "Test 3DS Validation Failed (Visa)",
    number: "4111 1111 1114 0003",
    expiration: "01/33",
    security_code: "333",
} satisfies Partial<OmiseCardFormInputs>;

const _mastercard3dsEnrollmentFailedTest = {
    name: "Test 3DS Enrollment Failed (Mastercard)",
    number: "5555 5511 1112 0002",
    expiration: "01/32",
    security_code: "321",
} satisfies Partial<OmiseCardFormInputs>;

export default function OmiseCardAddForm({
    actionsRef,
    config: { public_key, use_3ds_verification },
}: { config: OmiseCreditCardConfig } & AddMethodProps) {
    //#region Hooks
    const apiClient = useApiClient();
    const signal = useUnmountSignal();
    const { t } = useLingui();
    const form = useForm<OmiseCardFormInputs>({
        // DEV: Auto-fill with fake data for convenience of testing
        defaultValues: import.meta.env.DEV && !isTestEnvironment && !isBuiltRelease ? _visaTest : {},
    });
    usePreloadOmise();

    const [abortExpiryProcessing, setAbortExpiryProcessing] = useState(false);

    useImperativeHandle(actionsRef, () => ({
        async validate() {
            return await form.trigger(undefined, { shouldFocus: true });
        },
        needsPopUp() {
            void use_3ds_verification;
            // change later when we have migrated to new UI - needs to check payment method config (if 'use_3ds_verification' prop is set)
            return true;
        },
        async addPaymentMethod({ account, returnUrl, progress, popup }) {
            const billing_account_id = account.id;

            progress(t_LoadingPaymentProvider);
            const omise = await omiseLoader();
            omise.setPublicKey(public_key);

            const { name, number, expiration, security_code } = form.getValues();
            const [expiration_month, expiration_year] = splitExpiration(expiration);
            const tokenParameters: FixedOmiseTokenParameters = {
                name,
                number,
                expiration_month,
                expiration_year,
                security_code,
            };
            progress(t_ConnectingPaymentMethod);
            const token = await omiseCreateToken(omise, "card", tokenParameters);
            console.debug("Omise token response", token);

            progress(t_SavingInformation);
            const addCardResult = await addCardRequest(apiClient, {
                billing_account_id,
                token: token.id,
                processor_keyword: "omise_creditcard",
            });
            if (!addCardResult.is_verified) {
                progress(t_VerifyingPaymentMethod);
                const verifyResult = await cardVerifyRequest(apiClient, addCardResult.id, returnUrl, true);

                if (verifyResult.authorize_uri) {
                    progress(t_RedirectingToPaymentGateway);

                    invariant(popup, "Popup must be provided");
                    popup.handleRedirect(verifyResult.authorize_uri);

                    return await pollForOmiseCardValidationSuccess(
                        { signal, popup },
                        addCardResult.id,
                        async () => await cardListRequest(apiClient, account.id, signal),
                    );
                } else if (!verifyResult.card.is_verified) {
                    throw new Error("Unknown error in card verification");
                }
            }
        },
    }));
    //#endregion

    const content = (
        <WFakeForm className="flex flex-col gap-2" form={form}>
            <CardNameField form={form} name="name" autoFocus />
            <CardNumberField form={form} name="number" />
            <div className="flex flex-row gap-2">
                <Controller
                    control={form.control}
                    name="expiration"
                    rules={{
                        required: t(requiredMessage),
                        maxLength: 5,
                        validate: (v) => {
                            if (!/^\d{1,2}\/\d{1,2}$/.test(v)) {
                                return t`The format is MM/YY`;
                            }
                            const [month, year] = splitExpiration(v);
                            if (!month || month > 12) {
                                return t({
                                    comment: "Card expiration validation error",
                                    message: "Invalid month",
                                });
                            }
                            if (!year || year > 99) {
                                return t({
                                    comment: "Card expiration validation error",
                                    message: "Invalid year",
                                });
                            }
                            return true;
                        },
                    }}
                    render={({ field: { ref, value, onChange }, fieldState }) => (
                        <TextField className={T.TextField}>
                            <Label className={T.Label}>{cardExpiryLabel.label}</Label>
                            <Input
                                ref={ref}
                                className={T.Input}
                                placeholder={cardExpiryLabel.placeholder}
                                value={value}
                                onKeyDown={(e) => {
                                    setAbortExpiryProcessing(e.key === "Backspace" || e.key === "Delete");
                                }}
                                onChange={(e) => {
                                    let cleanedValue = e.target.value;
                                    if (!abortExpiryProcessing) {
                                        cleanedValue = cleanedValue.replace(/\/+/g, "").replace(/[^0-9/]/g, "");
                                        if (!/^.*\/.*$/.test(cleanedValue)) {
                                            if (cleanedValue.length >= 2) {
                                                cleanedValue = `${cleanedValue.slice(0, 2)}/${cleanedValue.slice(2)}`;
                                            }
                                        }
                                    }
                                    onChange(cleanedValue);
                                }}
                                maxLength={5}
                                autoComplete="cc-exp"
                            />
                            {fieldState.error && <ErrorText text={fieldState.error.message} />}
                        </TextField>
                    )}
                />
                <StandardField
                    form={form}
                    name="security_code"
                    {...cardCvcLabel}
                    autoComplete="cc-csc"
                    inputMode="numeric"
                    options={{
                        isRequired: true,
                        pattern: {
                            value: /^\d+$/,
                            message: t({
                                comment: "Card security code validation error",
                                message: "Enter digits only",
                            }),
                        },
                    }}
                />
            </div>
        </WFakeForm>
    );

    return <FormBox>{content}</FormBox>;
}

function splitExpiration(expiration: string): [number, number] {
    const parts = expiration.split("/");
    const twoParts = [parts[0], parts.slice(1).join("")];
    return twoParts.map((v) => parseInt(v, 10)) as [number, number];
}
